import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { ScribeState, TranscriptEntry } from '../../models/stt-chunked.model';
import { RecordingService, ChunkOptions } from './recording/recording.service';
import { UploadPipelineService } from './upload-pipeline/upload-pipeline.service';
import { CVA_CONSTANTS, MESSAGES } from '../../util/constants';

const MAX_OVERLAP_WORDS = 8;
@Injectable({
  providedIn: 'root'
})
export class ScribeService implements OnDestroy {
  readonly state$ = new BehaviorSubject<ScribeState>('idle');
  readonly transcript$ = new BehaviorSubject<string>('');
  readonly silentChunk$: Observable<{ count: number; limitReached: boolean }>;

  readonly error$ = new Subject<string>();
  // Non-fatal — one lost chunk shouldn't stop the session. Component should
  // show a soft toast, not treat this like error$ (which halts recording).
  readonly warning$ = new Subject<string>();
  // Assigned in the constructor, NOT as field initializers: with
  // useDefineForClassFields / ES2022 targets, field initializers run before
  // constructor parameter properties are assigned, so `this.pipeline` was undefined.
  readonly pending$: BehaviorSubject<number>;
  // Mic unplugged / audio lost mid-recording. RecordingService has already flushed
  // and torn down by the time this fires; the component must reset its own UI.
  readonly deviceDisconnected$: Observable<void>;
  private destroy$ = new Subject<void>();

  private currentSessionId = '';
  // start() re-entrancy guard (a 2nd start would overwrite currentSessionId and
  // make the first session's transcripts get filtered out).
  private starting = false;



  /**
  * Inbound trigger channel — any other component can call
  * `scribeService.stopRec.next('stopped')` to force-stop remotely,
  * same pattern as the old Azure service. Not auto-wired to stop()
  * internally — the consuming component owns that call so its own
  * timer/loading/notification bookkeeping runs correctly.
  */
  readonly stopRec: Subject<string> = new Subject();

  constructor(
    private recording: RecordingService,
    private pipeline: UploadPipelineService,
  ) {
    this.pending$ = this.pipeline.pending$;
    this.deviceDisconnected$ = this.recording.deviceDisconnected$.asObservable();
    this.silentChunk$ = this.recording.silentChunk$.asObservable();
    this.initListeners();
  }

  initListeners() {
    this.recording.chunk$
      .pipe(takeUntil(this.destroy$))
      .subscribe(chunk => this.pipeline.enqueue(chunk));

    // Wire transcript results → ordered array
    this.pipeline.transcript$
      .pipe(takeUntil(this.destroy$))
      .subscribe(entry => {
        if (entry.sessionId !== this.currentSessionId) return;
        //considering blank responses as a part of skipping the VAD and relying on the server response
        this.recording.noteTranscribed(!!entry.response?.trim());
        this.emitTranscript(entry);
      });

    this.pipeline.dropped$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ seq, sessionId, authMessage }) => {
        if (sessionId !== this.currentSessionId) return;
        console.warn(`[Scribe] chunk ${seq} dropped`);
        if (authMessage !== undefined) {
          this.warning$.next(authMessage || CVA_CONSTANTS.MESSAGE.SESSION_EXPIRED);
          return;
        }
        this.warning$.next(MESSAGES.AUDIO_CHUNK_DROPPED);
      });

    this.recording.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => this.state$.next(s as ScribeState));

    this.pipeline.isSessionActive = id => id === this.currentSessionId;
  }

  /**
   * @returns true only if a recording is actually running when this resolves
   * (fresh start or resume). false = nothing started — either an error was
   * routed to error$, or the call was ignored (already active / start in
   * progress). The caller MUST reset its loading UI on false.
   */
  async start(opts?: ChunkOptions): Promise<boolean> {
    if (this.starting) {
      console.warn('[Scribe] start() ignored — a start is already in progress');
      return false;
    }

    if (this.state$.value === 'paused') {
      try {
        await this.recording.resume();
        console.log('[Scribe] resumed from pause');
        return true;
      } catch (err) {
        console.warn('Resume failed, starting a new recording.', err);
        // Move recording out of 'paused' first. Otherwise the idle/stopped guard
        // below sees 'paused' and silently returns, leaving the caller's spinner stuck.
        this.recording.discard();
      }
    }

    // guard — don't allow start while already active
    if (this.state$.value !== 'idle' && this.state$.value !== 'stopped') {
      console.warn(`[Scribe] start() ignored — state is '${this.state$.value}'`);
      return false;
    }

    this.starting = true;
    // Everything that can throw lives inside the try — including session-id creation —
    // so failures reach error$ instead of escaping as a raw rejection.
    try {
      this.currentSessionId = this.newSessionId();
      this.transcript$.next('');
      console.log(`[Scribe] starting session ${this.currentSessionId}`);
      await this.recording.start(this.currentSessionId, opts);
      // Re-read as ScribeState: TS narrowed it to idle|stopped by the guard above, but it has changed since.
      return (this.state$.value as ScribeState) === 'recording';
    } catch (err) {
      console.error(err)
      const message =
        typeof err === 'string'
          ? err
          : err instanceof Error && err.message
            ? err.message
            : 'Microphone unavailable.';

      this.currentSessionId = '';
      this.error$.next(message);
      // State stays idle — no partial session left open
      return false;
    } finally {
      this.starting = false;
    }
  }

  pause(): void {
    if (this.state$.value !== 'recording') return;
    this.recording.pause();
  }

  async resume(): Promise<void> {
    if (this.state$.value !== 'paused') return;
    await this.recording.resume();
  }

  stop(): void {
    if (this.state$.value === 'idle') return;
    this.recording.stop();
    this.state$.next('stopped');
  }

  discard(): void {
    const id = this.currentSessionId;
    this.currentSessionId = '';
    this.pipeline.cancelSession(id); // aborts the in-flight request; queued chunks are skipped by isSessionActive
    if (this.state$.value === 'idle') return;
    this.recording.discard();
    // recording.discard() is a no-op when the recorder is already 'idle' (i.e. after
    // stop()), which would leave this mirror stuck on 'stopped'. Force it back.
    if ((this.state$.value as ScribeState) !== 'idle') this.state$.next('idle');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.error$.complete();
    this.warning$.complete();
    this.stopRec.complete();
    this.transcript$.complete();
    this.state$.complete();
  }

  /** crypto.randomUUID() only exists in secure contexts / newer browsers; getRandomValues does not have that limit. */
  private newSessionId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    const b = crypto.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40; // version 4
    b[8] = (b[8] & 0x3f) | 0x80; // variant
    const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  }

  private emitTranscript(entry: TranscriptEntry) {
    console.log(`processingTime:${entry.processingTime} seq:${entry.seq} durationSec:${entry.durationSec}`);
    this.transcript$.next(entry.response);
  }

  mergeChunkText(prev: string, next: string): string {
    prev = prev ?? '';
    const nxt = (next || '').trim();
    if (!nxt) return prev;
    const base = prev.replace(/\s+$/, '');
    const pt = Array.from(base.matchAll(/\S+/g));
    const nt = Array.from(nxt.matchAll(/\S+/g));
    if (!pt.length || !nt.length) return prev ? `${prev} ${nxt}` : nxt;

    const w = (m: RegExpMatchArray) => this.normWord(m[0]);
    const same = (a: string, b: string) => !!a && a === b;
    const end = (m: RegExpMatchArray) => (m.index ?? 0) + m[0].length;

    // last k words of prev == next[skip..skip+k-1]; skip=1 tolerates a garbled first word
    const tryMerge = (k: number, skip: number): string | null => {
      if (k > pt.length || skip + k > nt.length) return null;
      for (let i = 0; i < k - 1; i++) {
        if (!same(w(pt[pt.length - k + i]), w(nt[skip + i]))) return null;
      }
      const lastPrev = pt[pt.length - 1];
      const lastNext = nt[skip + k - 1];
      const a = w(lastPrev), b = w(lastNext);
      if (same(a, b)) {
        const rest = nxt.slice(end(lastNext)).trim();
        return rest ? `${base} ${rest}` : prev;
      }
      // min length 4 so "the" + "then" is not treated as a cut word
      if (a.length >= 4 && b.length > a.length && b.startsWith(a)) {
        return base.slice(0, lastPrev.index ?? 0) + nxt.slice(lastNext.index ?? 0);
      }
      return null;
    };

    for (let k = Math.min(MAX_OVERLAP_WORDS, pt.length, nt.length); k >= 2; k--) {
      for (const skip of [0, 1]) {
        const r = tryMerge(k, skip);
        if (r !== null) return r;
      }
    }
    return tryMerge(1, 0) ?? `${base} ${nxt}`;
  }

  private normWord(w: string): string {
    return w.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
  }
}
